<template>
  <div id="app">
    <router-view :shows="shows" :siteContent="siteContent" :merch="merch" />
  </div>
</template>

<script>
import { db } from './firebase'
import { collection, getDocs, doc, getDoc } from 'firebase/firestore'

// Default merch items (fallback if Firestore is empty)
const defaultMerch = [
  {
    id: 1,
    name: "LOGO T-SHIRT",
    link: "https://nadezhda.bandcamp.com/merch/logo-t-shirt",
    imageLink: "../assets/graphics/merch/NDZ_T.png",
    canPurchase: true
  },
  {
    id: 2,
    name: "ONE OF US T-SHIRT",
    link: "https://nadezhda.bandcamp.com/merch/nadezhda-one-of-us-t-shirt",
    imageLink: "../assets/graphics/merch/NDZ_OneOfUs.png",
    canPurchase: true
  },
  {
    id: 3,
    name: "ORANGE TOTE",
    link: "https://nadezhda.bandcamp.com/merch/nadezhda-orange-tote",
    imageLink: "../assets/graphics/merch/NDZ_Tote.png",
    canPurchase: true
  },
  {
    id: 4,
    name: "IRON ON PATCH",
    link: "https://nadezhda.bandcamp.com/merch/nadezhda-goodside-patch",
    imageLink: "../assets/graphics/merch/NDZ_Patch.png",
    canPurchase: true
  },
  {
    id: 5,
    name: "NADDY BIC LIGHTER",
    link: "",
    imageLink: "../assets/graphics/merch/NDZ_Bic.png",
    canPurchase: false
  }
]

export default {
  name: 'App',
  data() {
    return {
      shows: [], // {'date', 'time', 'venue', 'city', 'tickets'}
      merch: defaultMerch,
      siteContent: {
        videoTitle: 'BUSY BEING BORED VIDEO OUT NOW',
        videoUrl: 'https://www.youtube.com/embed/r78IdK2ycYY',
        playlistTitle: 'BATTERY EP OUT NOW',
        playlistUrl: 'https://open.spotify.com/embed/album/60qvcz15u7N34yDKf7Icdf?utm_source=generator',
        musicImageUrl: ''
      }
    }
  },
  async mounted() {
    await Promise.all([
      this.fetchShows(),
      this.fetchSiteContent(),
      this.fetchMerch()
    ])
  },
  methods: {
    async fetchMerch() {
      try {
        const merchRef = collection(db, 'merch')
        const snapshot = await getDocs(merchRef)
        
        if (!snapshot.empty) {
          this.merch = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
        }
        // If empty, keep default merch
      } catch (error) {
        console.error('Error fetching merch:', error)
        // Keep default merch on error
      }
    },
    
    async fetchSiteContent() {
      try {
        const docRef = doc(db, 'siteContent', 'main')
        const docSnap = await getDoc(docRef)
        
        if (docSnap.exists()) {
          const data = docSnap.data()
          this.siteContent = {
            videoTitle: data.videoTitle || this.siteContent.videoTitle,
            videoUrl: data.videoUrl || this.siteContent.videoUrl,
            playlistTitle: data.playlistTitle || this.siteContent.playlistTitle,
            playlistUrl: data.playlistUrl || this.siteContent.playlistUrl,
            musicImageUrl: data.musicImageUrl || this.siteContent.musicImageUrl
          }
        }
      } catch (error) {
        console.error('Error fetching site content:', error)
        // Keep default values on error
      }
    },
    
    parseShowDate(dateString) {
      if (!dateString) return null
      
      // If it's a date range (e.g., "December 19 - 21"), take the first date
      if (dateString.includes(' - ')) {
        dateString = dateString.split(' - ')[0]
      }
      
      // Remove any ordinal suffixes (1st, 2nd, 3rd, 4th, etc.)
      dateString = dateString.replace(/(\d+)(st|nd|rd|th)/gi, '$1')
      
      // Try parsing the date directly (handles "January 15, 2025")
      let date = new Date(dateString)
      if (!isNaN(date.getTime())) {
        return date
      }
      
      return null
    },
    
    async fetchShows() {
      try {
        const showsRef = collection(db, 'shows')
        const snapshot = await getDocs(showsRef)
        
        if (!snapshot.empty) {
          const showsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          
          const today = new Date()
          today.setHours(0, 0, 0, 0)
          
          // Sort shows: upcoming first (by date asc), then past shows (by date desc)
          this.shows = showsData.sort((a, b) => {
            const dateA = this.parseShowDate(a.date)
            const dateB = this.parseShowDate(b.date)
            
            // Handle unparseable dates - push to end
            if (!dateA && !dateB) return 0
            if (!dateA) return 1
            if (!dateB) return -1
            
            const isPastA = dateA < today
            const isPastB = dateB < today

            // If one is past and one is upcoming, upcoming comes first
            if (isPastA && !isPastB) return 1
            if (!isPastA && isPastB) return -1

            // If both are upcoming, sort by date ascending (soonest first)
            if (!isPastA && !isPastB) {
              return dateA - dateB
            }
            
            // If both are past, sort by date descending (most recent first)
            return dateB - dateA
          })
        }
        // If empty, shows array stays empty
      } catch (error) {
        console.error('Error fetching shows:', error)
      }
    }
  }
}
</script>

<style>

@font-face {
  font-family: 'BNAxelGroteskBold';
  src: url('./assets/fonts/BNAxelGrotesk-Bold.woff') format('woff');
}

@font-face {
  font-family: 'BobbiTheHippie';
  src: url('./assets/fonts/BobbiTheHippie.woff') format('woff');
}

@font-face {
  font-family: 'EditorialNewVariable';
  src: url('./assets/fonts/EditorialNew-Variable.woff') format('woff');
}


@font-face {
  font-family: 'NeueMontrealBold';
  src: url('./assets/fonts/NeueMontreal-Bold.woff') format('woff');
}

@font-face {
  font-family: 'NeueMontrealRegular';
  src: url('./assets/fonts/NeueMontreal-Regular.woff') format('woff');
}

body {
  margin: 0;
  padding: 0;
  height: 100%;
  width: 100%;
  overflow-x: hidden;
  background: #f06825;
}

#app {
  background: #f06825;
  overflow-x: hidden;
}
</style>
